import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../axios';

export interface Author {
  id: string;
  email: string;
  userId: string | null;
  status: 'pending' | 'active' | 'revoked';
  invitedBy: string;
  invitedAt: string;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InviteAuthorData {
  email: string;
}

export interface ActivateAuthorData {
  userId: string;
}

export const useAuthors = (status?: string) => {
  return useQuery({
    queryKey: ['authors', status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      
      const response = await api.get(`/authors?${params.toString()}`);
      return response.data.authors as Author[];
    },
  });
};

export const useInviteAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: InviteAuthorData) => {
      const response = await api.post('/authors', data);
      return response.data as Author;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
    },
  });
};

export const useActivateAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ActivateAuthorData }) => {
      const response = await api.patch(`/authors/${id}/activate`, data);
      return response.data as Author;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
    },
  });
};

export const useRevokeAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.patch(`/authors/${id}/revoke`);
      return response.data as Author;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
    },
  });
};

export const useReactivateAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.patch(`/authors/${id}/reactivate`);
      return response.data as Author;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
    },
  });
};

export const useCheckAuthor = (email: string) => {
  return useQuery({
    queryKey: ['authors', 'check', email],
    queryFn: async () => {
      const response = await api.get(`/authors/check/${email}`);
      return response.data as { exists: boolean; author: Author | null };
    },
    enabled: !!email,
  });
};
